import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type CalendarEventAccessJSON = z.infer<typeof CalendarEventAccessSchema.CalendarEventAccessJSONZ>;
export type CalendarEventAccessParams = z.infer<typeof CalendarEventAccessSchema.CalendarEventAccessZ>;

export default class CalendarEventAccessSchema {
  public static readonly CalendarEventAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.CalendarEventAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
  });

  public static readonly CalendarEventAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.CalendarEventAccess),
    accessLevel: z.enum(AccessLevel).optional(),
  });

  public static validate(values: unknown): CalendarEventAccessParams {
    return parseOrThrow(CalendarEventAccessSchema.CalendarEventAccessZ, values, {
      logError: true,
      schemaName: "CalendarEventAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): CalendarEventAccessJSON {
    return parseOrThrow(CalendarEventAccessSchema.CalendarEventAccessJSONZ, values, {
      logError: true,
      schemaName: "CalendarEventAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static deserialize(data: CalendarEventAccessJSON): CalendarEventAccessParams {
    const parsed = CalendarEventAccessSchema.validateJSON(data);
    return CalendarEventAccessSchema.codec.decode(parsed);
  }

  public static serialize(resource: CalendarEventAccessParams): CalendarEventAccessJSON {
    const parsed = CalendarEventAccessSchema.validate(resource);
    return CalendarEventAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    CalendarEventAccessSchema.CalendarEventAccessJSONZ,
    CalendarEventAccessSchema.CalendarEventAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.CalendarEventAccess as const,
        accessLevel: data.accessLevel ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.CalendarEventAccess as const,
        accessLevel: resource.accessLevel,
      }),
    },
  );
}
